<template>
  <form class="calculator" @submit.prevent="evaluate" @reset.prevent="clearAll">
    <ECalculatorDisplay
      ref="display"
      :expression="expression"
      :output="output"
      :interacting="interacting"
      :error="error"
    />
    <ECalculatorButtons
      :angle-units="angleUnits"
      :decimal-separator="decimalSeparator"
      :inverted-functions="invertedFunctions"
      @insert="insert"
      @insert-digit="insertDigit"
      @insert-with-brackets="insertWithBrackets"
      @insert-with-space="insertWithSpace"
      @invert-functions="invertFunctions"
      @set-angle-units="setAngleUnits"
    />
  </form>
</template>

<script>
import { mapState } from 'vuex';

import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';
import { localisePlainNumber } from '~~/common/js/universal/intl';

import ECalculatorButtons from './buttons';
import ECalculatorDisplay from './display';
import { PRECISION, RADIANS, DECIMAL_SEPARATOR, THOUSANDS_SEPARATOR } from './constants';
import { createCustomFunctions, createAliases } from './custom-functions';
import { InvalidExpressionError, DivisionByZeroError } from './errors';
import { preprocessExpression, preprocessInitialExpression } from './pre-processing';

export default {
  name: 'ECalculator',
  components: {
    ECalculatorButtons,
    ECalculatorDisplay,
  },
  props: {
    initialExpression: {
      type: String,
      default: '',
    },
  },
  data: ({ prepareInitialExpression }) => ({
    expression: prepareInitialExpression(),
    // numeric value of the calculation result
    ans: 0,
    // "printed" value of the calculation result
    output: '',
    error: null,
    // whether the user is currently building an expression
    // i.e. the curent expression hasn't been evaluated
    interacting: false,
    // maps various human-readable characters to MathJS symbols
    characterMap: {},
    decimalSeparator: '.',
    invertedFunctions: false,
    // keeps track of where in the expression to insert symbols
    bracketDepth: 0,
    angleUnits: RADIANS,
  }),
  computed: {
    ...mapState('global', ['locale']),
    preparedExpression() {
      return preprocessExpression(this.expression, this.characterMap);
    },
    // This regexp helps us decide when a space should be added
    // before a digit is inserted using a button.
    digitRegexp() {
      return new RegExp(`[\\(0-9${this.decimalSeparator}\\^E]`);
    },
    needSpaceBeforeInsert() {
      const lastChar = this.expression[this.expression.length - this.bracketDepth - 1];
      return !lastChar || this.digitRegexp.test(lastChar) || !this.interacting;
    },
  },
  async mounted() {
    const { create, all } = await import('mathjs');

    // Custom functions let us implement the global Radians/Degrees mode switch.
    const customFunctions = createCustomFunctions({ angleUnits: this.angleUnits });
    this.math = create(all, { precision: PRECISION });
    this.math.import(customFunctions, { override: true });
    // The character map is used to post-process the expression before evaluating it.
    this.createCharacterMap(customFunctions);
    // We announce the first successful evaluation
    // so the parent can decide if it wants to show the calculator.
    const success = this.evaluate();
    this.$emit('initial-parse', { success });
  },
  methods: {
    prepareInitialExpression() {
      return preprocessInitialExpression(this.initialExpression);
    },
    evaluate() {
      this.error = null;
      if (this.preparedExpression === '') {
        return true;
      }

      try {
        try {
          this.ans = this.math.evaluate(this.preparedExpression, { Ans: this.ans });
        } catch (err) {
          throw new InvalidExpressionError();
        }

        if (this.ans === Infinity) {
          throw new DivisionByZeroError();
        }

        if (isNaN(this.ans)) {
          throw new InvalidExpressionError();
        }
        // large numbers (e.g. 5,000,000,000,000) might not fit into the display on mobile
        // so we format those in scientific notation (e.g. 5e12)
        const notation = Math.abs(this.ans) >= 10 ** PRECISION ? 'scientific' : 'standard';
        const localised = localisePlainNumber(
          this.locale, this.ans,
          { maximumFractionDigits: PRECISION, notation },
        );

        if (this.interacting) {
          this.focusDisplay();
        }

        this.output = localised;
        this.interacting = false;
        this.resetInputState();

        return true;
      } catch (err) {
        this.ans = 0;
        this.error = err;
      }

      return false;
    },
    createCharacterMap(customFunctions) {
      const numberFormat = new Intl.NumberFormat([this.locale]);
      const parts = numberFormat.formatToParts(1000.1);
      // eslint-disable-next-line prefer-destructuring
      this.decimalSeparator = parts.find(({ type }) => type === 'decimal')?.value;
      this.characterMap = {
        ...createAliases(customFunctions),
        [this.decimalSeparator]: DECIMAL_SEPARATOR,
      };

      const thousandsSeparator = parts.find(({ type }) => type === 'group')?.value;
      if (thousandsSeparator) {
        this.characterMap[thousandsSeparator] = THOUSANDS_SEPARATOR;
      }
    },
    insert(character, resetIfNotInteracting = false) {
      if (!this.interacting) {
        this.expression = this.ans.toString();
        this.interacting = true;

        if (resetIfNotInteracting) {
          this.resetInput();
        }
      }

      if (this.bracketDepth === 0) {
        this.expression += character;
      } else {
        const insertIndex = this.expression.length - this.bracketDepth;
        const before = this.expression.slice(0, insertIndex);
        const after = this.expression.slice(insertIndex);
        this.expression = before + character + after;
      }
    },
    insertWithSpace(character, resetIfNotInteracting) {
      this.insert(` ${character}`, resetIfNotInteracting);
    },
    insertDigit(character) {
      if (this.needSpaceBeforeInsert) {
        this.insert(character, true);
      } else {
        this.insertWithSpace(character, true);
      }
    },
    insertWithBrackets(character) {
      this.insertDigit(`${character}()`);
      this.bracketDepth += 1;
    },
    invertFunctions() {
      this.invertedFunctions = !this.invertedFunctions;
    },
    clearAll() {
      this.ans = 0;
      this.error = null;
      this.output = '';
      this.interacting = false;
      this.resetInput();
    },
    setAngleUnits(units) {
      this.angleUnits = units;
      this.math.import(createCustomFunctions({ angleUnits: this.angleUnits }), { override: true });
      this.evaluate();
      this.focusDisplay();
    },
    resetInputState() {
      this.bracketDepth = 0;
      this.invertedFunctions = false;
    },
    resetInput() {
      this.expression = '';
      this.resetInputState();
    },
    focusDisplay() {
      focusFocusableChild(this.$refs.display.$el);
    },
  },
};
</script>
